/*
  kr-toc: índice de contenidos de la sección kr-policy.
  <kr-toc data-target="#id-del-contenido" data-selector="h2, h3" hidden> ... <ol data-kr-toc-list></ol> ... </kr-toc>
  - Busca los encabezados del contenido (data-selector), les asigna un id (slug del texto) si no lo tienen
    y crea un enlace por encabezado (li.kr-policy__toc-item--h2 / --h3 > a.kr-policy__toc-link).
  - Con menos de 2 encabezados el índice se queda oculto (atributo hidden).
  - Resalta el apartado visible (kr-policy__toc-link--active) con un IntersectionObserver y, al pulsar un enlace,
    hace scroll suave (o inmediato con prefers-reduced-motion), actualiza la URL y enfoca el encabezado.
*/
const MIN_HEADINGS = 2;

class KrToc extends HTMLElement {
  #controller;
  #observer;
  #links = new Map();

  connectedCallback() {
    const target = document.querySelector(this.dataset.target ?? '');
    const list = this.querySelector('[data-kr-toc-list]');
    if (!(target instanceof HTMLElement) || !(list instanceof HTMLElement)) return;

    const selector = this.dataset.selector || 'h2, h3';
    const headings = [...target.querySelectorAll(selector)].filter((heading) => heading.textContent?.trim());
    if (headings.length < MIN_HEADINGS) return;

    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const fragment = document.createDocumentFragment();
    const usedIds = new Set();

    for (const heading of headings) {
      const id = this.#ensureId(heading, usedIds);
      const item = document.createElement('li');
      item.className = `kr-policy__toc-item kr-policy__toc-item--${heading.tagName.toLowerCase()}`;
      const link = document.createElement('a');
      link.className = 'kr-policy__toc-link';
      link.href = `#${id}`;
      link.textContent = heading.textContent?.trim() ?? '';
      item.append(link);
      fragment.append(item);
      this.#links.set(id, link);
    }

    list.replaceChildren(fragment);
    this.hidden = false;
    this.addEventListener('click', this.#handleClick, { signal });
    this.#observeHeadings(headings);
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#observer?.disconnect();
    this.#links.clear();
  }

  // Id estable a partir del texto del encabezado: minúsculas, sin acentos, guiones; sufijo numérico si se repite.
  #ensureId(heading, usedIds) {
    let id = heading.id;
    if (!id) {
      const base =
        heading.textContent
          ?.trim()
          .toLowerCase()
          .normalize('NFD')
          .replace(/[̀-ͯ]/g, '')
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '')
          .slice(0, 60) || 'section';
      id = base;
      let suffix = 2;
      while (usedIds.has(id) || document.getElementById(id)) id = `${base}-${suffix++}`;
      heading.id = id;
    }
    usedIds.add(id);
    return id;
  }

  #handleClick = (event) => {
    const link = event.target?.closest?.('.kr-policy__toc-link');
    if (!(link instanceof HTMLAnchorElement)) return;
    const heading = document.getElementById(decodeURIComponent(link.hash.slice(1)));
    if (!heading) return;
    event.preventDefault();
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    heading.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    history.replaceState(null, '', link.hash);
    heading.setAttribute('tabindex', '-1');
    heading.focus({ preventScroll: true });
  };

  // El encabezado que entra en la franja superior de la ventana (10 %–20 %) pasa a ser el activo.
  #observeHeadings(headings) {
    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) if (entry.isIntersecting) this.#setActive(entry.target.id);
      },
      { rootMargin: '-10% 0px -80% 0px', threshold: 0 }
    );
    for (const heading of headings) this.#observer.observe(heading);
  }

  #setActive(id) {
    for (const [linkId, link] of this.#links) link.classList.toggle('kr-policy__toc-link--active', linkId === id);
  }
}

customElements.get('kr-toc') || customElements.define('kr-toc', KrToc);
